import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Send, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const blank = {
  nom: "",
  email: "",
  telephone: "",
  password: "",
  role: "gestionnaire",
  permissions: null,
  actif: true,
  notifications: { email: true, push: false, callmebot: false },
  callmebot_phone: "",
  callmebot_apikey: "",
};

export default function AdminUsers() {
  const [meta, setMeta] = useState({ modules: [], roles: {} });
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)), []);

  useEffect(() => {
    api.get("/admin/meta/roles").then(({ data }) => setMeta(data));
    load();
  }, [load]);

  const perms = form.permissions ?? meta.roles?.[form.role]?.permissions ?? [];

  const togglePerm = (key) => {
    const next = perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];
    setForm({ ...form, permissions: next });
  };

  const save = async () => {
    if (!form.nom.trim()) return toast.error("Nom requis");
    if (!form.email && !form.telephone) return toast.error("Email ou téléphone requis");
    setBusy(true);
    try {
      const payload = { ...form, permissions: perms };
      if (editId) await api.put(`/admin/users/${editId}`, payload);
      else await api.post("/admin/users", payload);
      toast.success(editId ? "Utilisateur mis à jour" : "Utilisateur créé");
      setForm(blank);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const edit = (u) => {
    setEditId(u.id);
    setForm({
      ...blank,
      ...u,
      email: u.email || "",
      telephone: u.telephone || "",
      password: "",
      callmebot_phone: u.callmebot_phone || "",
      callmebot_apikey: "",
      notifications: { email: true, push: false, callmebot: false, ...(u.notifications || {}) },
    });
  };

  const testCallmebot = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/test-callmebot`);
    data.envoye ? toast.success("Message WhatsApp envoyé") : toast.error(`Échec : ${data.detail}`);
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <div className="bk-label mb-2">Équipe BKPRO</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Utilisateurs & droits</h1>
        <p className="text-sm text-slate-600 mt-2">
          Un rôle définit les droits par défaut, ajustables module par module. Les notifications WhatsApp passent par CallMeBot (numéro + clé
          personnelle à obtenir auprès du bot CallMeBot).
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="bk-label">{editId ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</div>
          <Input data-testid="user-nom-input" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom et prénom" className="h-11" />
          <Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (identifiant de connexion)" className="h-11" />
          <Input data-testid="user-tel-input" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Téléphone (identifiant alternatif)" className="h-11" />
          <Input
            data-testid="user-password-input"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={editId ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe (8 caractères min.)"}
            className="h-11"
          />
          <select
            data-testid="user-role-select"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value, permissions: meta.roles?.[e.target.value]?.permissions || [] })}
            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {Object.entries(meta.roles || {}).map(([key, r]) => (
              <option key={key} value={key}>{r.label}</option>
            ))}
          </select>

          <div className="pt-1">
            <div className="bk-label mb-2">Droits d'accès</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {meta.modules?.map((m) => (
                <label key={m.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox data-testid={`perm-${m.key}`} checked={perms.includes(m.key)} onCheckedChange={() => togglePerm(m.key)} />
                  {m.label}
                </label>
              ))}
            </div>
          </div>

          <div className="pt-1">
            <div className="bk-label mb-2">Notifications</div>
            <div className="flex flex-wrap gap-4">
              {["email", "push", "callmebot"].map((canal) => (
                <label key={canal} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox
                    data-testid={`notif-${canal}`}
                    checked={!!form.notifications[canal]}
                    onCheckedChange={(v) => setForm({ ...form, notifications: { ...form.notifications, [canal]: !!v } })}
                  />
                  {canal === "callmebot" ? "WhatsApp (CallMeBot)" : canal === "push" ? "Push navigateur" : "Email"}
                </label>
              ))}
            </div>
          </div>

          {form.notifications.callmebot && (
            <div className="space-y-2">
              <Input data-testid="user-callmebot-phone" value={form.callmebot_phone} onChange={(e) => setForm({ ...form, callmebot_phone: e.target.value })} placeholder="Numéro WhatsApp international (33612345678)" className="h-11" />
              <Input data-testid="user-callmebot-key" value={form.callmebot_apikey} onChange={(e) => setForm({ ...form, callmebot_apikey: e.target.value })} placeholder="Clé API CallMeBot" className="h-11" />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox data-testid="user-actif" checked={form.actif} onCheckedChange={(v) => setForm({ ...form, actif: !!v })} /> Compte actif
          </label>

          <div className="flex gap-2 pt-1">
            <Button data-testid="user-save-btn" onClick={save} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC]">
              {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Plus size={15} className="mr-1" />} Enregistrer
            </Button>
            {editId && <Button variant="outline" onClick={() => { setEditId(null); setForm(blank); }}>Annuler</Button>}
          </div>
        </div>

        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => edit(u)} className="text-left min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <UserCog size={14} /> {u.nom || u.email}
                    {u.actif === false && <span className="text-xs text-red-600">(désactivé)</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {u.email || u.telephone} · {meta.roles?.[u.role]?.label || u.role} · {(u.permissions || []).length} droits
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Notifs : {Object.entries(u.notifications || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "aucune"}
                    {u.callmebot_apikey_defini ? " · CallMeBot configuré" : ""}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {u.notifications?.callmebot && (
                    <button data-testid={`user-test-callmebot-${u.id}`} onClick={() => testCallmebot(u)} className="p-2 text-slate-400 hover:text-[#005CFF]" title="Tester WhatsApp">
                      <Send size={14} />
                    </button>
                  )}
                  <button
                    data-testid={`user-delete-${u.id}`}
                    onClick={async () => {
                      try {
                        await api.delete(`/admin/users/${u.id}`);
                        load();
                        toast.success("Utilisateur supprimé");
                      } catch (e) {
                        toast.error(errMsg(e, "Suppression impossible"));
                      }
                    }}
                    className="p-2 text-slate-300 hover:text-red-500"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
